import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, MAGAZZINI, STAGIONI, MARCHI_PNEUMATICO, getScaffaliForMagazzino } from "@/lib/api";
import { ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const empty = {
  marca: "Pirelli",
  modello: "",
  misura: "",
  indice_carico: "",
  indice_velocita: "",
  stagionalita: "Estivo",
  magazzino: "Vasto",
  settore: 1,
  scaffale: "A",
  quantita: 4,
  note: "",
};

export default function MagazzinoForm() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      api.get(`/inventory/${id}`).then(({ data }) => setForm(data))
        .catch(() => toast.error("Errore caricamento articolo"));
    }
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.marca.trim()) return toast.error("Marca obbligatoria");
    if (!form.misura.trim()) return toast.error("Misura obbligatoria");
    if (!editing && (Number(form.quantita) || 0) <= 0) return toast.error("Quantita' iniziale deve essere maggiore di 0");
    setSaving(true);
    try {
      const payload = {
        ...form,
        marca: form.marca.trim(),
        modello: form.modello.trim(),
        misura: form.misura.trim(),
        indice_carico: (form.indice_carico || "").trim(),
        indice_velocita: (form.indice_velocita || "").trim().toUpperCase(),
        quantita: Number(form.quantita) || 0,
        settore: Number(form.settore) || 1,
      };
      if (editing) {
        await api.put(`/inventory/${id}`, payload);
        toast.success("Articolo aggiornato");
      } else {
        await api.post("/inventory", payload);
        toast.success("Gomme caricate in magazzino");
      }
      navigate("/magazzino");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const scaffali = getScaffaliForMagazzino(form.magazzino);

  return (
    <div className="space-y-6" data-testid="magazzino-form-page">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-button"
        className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest hover:text-[#E60000]"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Indietro
      </button>

      <div>
        <div className="text-[11px] font-black uppercase tracking-[0.3em] text-[#E60000]">
          {editing ? "Modifica articolo" : "Carica nuove gomme"}
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter mt-1">
          {editing ? "Articolo Magazzino" : "Nuovo Arrivo"}
        </h1>
      </div>

      <form onSubmit={save} className="space-y-6">
        <Section title="Pneumatico">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Marca *</Label>
              <select
                data-testid="mag-marca"
                value={form.marca}
                onChange={(e) => set("marca", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
              >
                {MARCHI_PNEUMATICO.map((m) => <option key={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <Label>Modello</Label>
              <input
                data-testid="mag-modello"
                value={form.modello}
                onChange={(e) => set("modello", e.target.value)}
                placeholder="Es. Pilot Sport, Cinturato P7..."
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000]"
              />
            </div>
            <div>
              <Label>Misura *</Label>
              <input
                data-testid="mag-misura"
                value={form.misura}
                onChange={(e) => set("misura", e.target.value)}
                placeholder="205/55 R16"
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-black uppercase"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Indice carico</Label>
                <input
                  data-testid="mag-indice-carico"
                  value={form.indice_carico}
                  onChange={(e) => set("indice_carico", e.target.value)}
                  placeholder="91"
                  className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-bold"
                />
              </div>
              <div>
                <Label>Indice velocita'</Label>
                <input
                  data-testid="mag-indice-velocita"
                  value={form.indice_velocita}
                  onChange={(e) => set("indice_velocita", e.target.value.toUpperCase())}
                  placeholder="V"
                  maxLength={3}
                  className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-black uppercase"
                />
              </div>
            </div>
            <div>
              <Label>Stagionalita'</Label>
              <select
                data-testid="mag-stagionalita"
                value={form.stagionalita}
                onChange={(e) => set("stagionalita", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
              >
                {STAGIONI.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label>{editing ? "Quantita' disponibile *" : "Quantita' in arrivo *"}</Label>
              <input
                data-testid="mag-quantita"
                type="number"
                min="0"
                value={form.quantita}
                onChange={(e) => set("quantita", e.target.value)}
                className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-black text-lg"
              />
            </div>
          </div>
        </Section>

        <Section title="Posizione">
          <div className="space-y-4">
            <div>
              <Label>Magazzino</Label>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                {MAGAZZINI.map((m) => (
                  <button
                    type="button"
                    key={m}
                    data-testid={`mag-magazzino-${m}`}
                    onClick={() => {
                      set("magazzino", m);
                      const valid = getScaffaliForMagazzino(m);
                      if (!valid.includes(form.scaffale)) set("scaffale", "A");
                    }}
                    className={`border-2 border-black p-4 font-display font-black uppercase text-lg transition-colors ${
                      form.magazzino === m ? "bg-[#E60000] text-white" : "bg-white text-black hover:bg-black hover:text-white"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div>
                <Label>Settore</Label>
                <input
                  data-testid="mag-settore"
                  type="number"
                  min="1"
                  max="121"
                  value={form.settore}
                  onChange={(e) => set("settore", e.target.value)}
                  className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] font-mono-data font-black"
                />
              </div>
              <div>
                <Label>Scaffale</Label>
                <select
                  data-testid="mag-scaffale"
                  value={form.scaffale}
                  onChange={(e) => set("scaffale", e.target.value)}
                  className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] bg-white font-bold"
                >
                  {scaffali.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
          </div>
        </Section>

        <Section title="Note">
          <textarea
            data-testid="mag-note"
            rows={3}
            value={form.note}
            onChange={(e) => set("note", e.target.value)}
            placeholder="Note (fornitore, lotto, prezzo di acquisto, ecc.)"
            className="w-full border-2 border-black p-3 outline-none focus:border-[#E60000] resize-none"
          />
        </Section>

        <div className="flex justify-end gap-3 sticky bottom-0 bg-white border-t-4 border-black -mx-4 md:-mx-8 px-4 md:px-8 py-4">
          <button
            type="button"
            onClick={() => navigate("/magazzino")}
            className="border-2 border-black px-5 py-3 font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white"
          >
            Annulla
          </button>
          <button
            type="submit"
            data-testid="mag-submit"
            disabled={saving}
            className="bg-[#E60000] text-white border-2 border-black px-6 py-3 font-bold uppercase tracking-widest hover:bg-black inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Save className="w-4 h-4" strokeWidth={3} />
            {saving ? "Salvataggio..." : (editing ? "Aggiorna" : "Carica in magazzino")}
          </button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="border-2 border-black bg-white p-5 md:p-6">
    <h2 className="font-display font-black text-lg uppercase tracking-tight mb-4 pb-3 border-b-2 border-black">
      {title}
    </h2>
    {children}
  </section>
);

const Label = ({ children }) => (
  <label className="block text-[10px] font-black uppercase tracking-widest mb-1.5">{children}</label>
);
